import { css, keyframes, useTheme } from "@emotion/react";
import { Formik, Form } from "formik";
import kebabCase from "lodash/fp/kebabCase";
import { Fragment } from "react";
import * as Yup from "yup";

import {
  Alert,
  Button,
  Stack,
  handleComponentsProp,
  useThemeProps,
  MaybeFragment,
} from "../../wsui/base";

import FormModuleCheckboxField from "./FormModuleCheckboxField.jsx";
import FormModuleCustomContentField from "./FormModuleCustomContentField.jsx";
import FormModuleInputField from "./FormModuleInputField.jsx";
import FormModuleMessageField from "./FormModuleMessageField.jsx";
import FormModuleRadioField from "./FormModuleRadioField.jsx";
import FormModuleSelectField from "./FormModuleSelectField.jsx";
import FormModuleSenderField from "./FormModuleSenderField.jsx";

function buildInitialValues({ fields }) {
  let initialValues = {};
  fields.forEach((field) => {
    switch (field.fieldType) {
      case "sender":
        {
          let { fields: includedFields } = field;
          if (includedFields && includedFields.includes("firstname")) {
            initialValues["fornamn"] = "";
          }
          if (includedFields && includedFields.includes("lastname")) {
            initialValues["efternamn"] = "";
          }
          if (includedFields && includedFields.includes("email")) {
            initialValues["e-post"] = "";
          }
          if (includedFields && includedFields.includes("phone")) {
            initialValues["telefonnummer"] = "";
          }
          if (includedFields && includedFields.includes("address")) {
            initialValues["adress"] = {
              gatuadress: "",
              postnummer: "",
              stad: "",
            };
          }
        }
        break;
      case "message":
      case "input":
      case "radio":
      case "select":
        {
          let { label } = field;
          initialValues[kebabCase(label)] = "";
        }
        break;
      case "checkbox":
        {
          let { label } = field;
          initialValues[kebabCase(label)] = [];
        }
        break;
      default: {
        // Skip
      }
    }
  });
  return initialValues;
}

function setRequiredOrNullable(schema, required) {
  if (required) {
    schema = schema.required("Du måste fylla i fältet ${label}");
  } else {
    schema = schema.nullable();
  }
  return schema;
}

function buildSchema({ fields }) {
  let schemaShape = {};
  fields.forEach((field) => {
    switch (field.fieldType) {
      case "sender":
        {
          let { fields: includedFields, requiredFields } = field;
          if (includedFields && includedFields.includes("firstname")) {
            schemaShape["fornamn"] = setRequiredOrNullable(
              Yup.string(),
              requiredFields && requiredFields.includes("firstname"),
            );
          }
          if (includedFields && includedFields.includes("lastname")) {
            schemaShape["efternamn"] = setRequiredOrNullable(
              Yup.string(),
              requiredFields && requiredFields.includes("lastname"),
            );
          }
          if (includedFields && includedFields.includes("email")) {
            schemaShape["e-post"] = setRequiredOrNullable(
              Yup.string().email("Fyll i en giltig e-postadress"),
              requiredFields && requiredFields.includes("email"),
            );
          }
          if (includedFields && includedFields.includes("phone")) {
            schemaShape["telefonnummer"] = setRequiredOrNullable(
              Yup.string(),
              requiredFields && requiredFields.includes("phone"),
            );
          }
          if (includedFields && includedFields.includes("address")) {
            let required = requiredFields && requiredFields.includes("address");
            schemaShape["adress"] = Yup.object().shape({
              gatuadress: setRequiredOrNullable(Yup.string(), required),
              postnummer: setRequiredOrNullable(Yup.string(), required),
              stad: setRequiredOrNullable(Yup.string(), required),
            });
          }
        }
        break;
      case "input":
        {
          let {
            label,
            required,
            valueType,
            minTimeValue,
            maxTimeValue,
            minValue,
            maxValue,
          } = field;
          let schema;
          switch (valueType) {
            case "range":
            case "number":
              schema = Yup.number().label(label);
              if (minValue != null) {
                schema = schema.min(
                  minValue,
                  "${label} får inte vara mindre än ${min}",
                );
              }
              if (maxValue != null) {
                schema = schema.max(
                  maxValue,
                  "${label} får inte vara större än ${max}",
                );
              }
              break;
            case "date":
              schema = Yup.date().label(label);
              if (minValue != null) {
                schema = schema.min(
                  minValue,
                  `\${label} får inte vara före ${String(minValue).replace(
                    /^(\d{4})(\d{2})(\d{2})$/,
                    "$1-$2-$3",
                  )}`,
                );
              }
              if (maxValue != null) {
                schema = schema.max(
                  maxValue,
                  `\${label} får inte vara efter ${String(maxValue).replace(
                    /^(\d{4})(\d{2})(\d{2})$/,
                    "$1-$2-$3",
                  )}`,
                );
              }
              break;
            case "time":
              schema = Yup.string().label(label);
              if (minTimeValue != null) {
                schema = schema.test(
                  "minTime",
                  `\${label} får inte vara före ${minTimeValue}`,
                  (value) =>
                    // We can’t be sure if the values contain seconds, so we
                    // place the digits after the decimal point before we
                    // compare them as numbers
                    Number("0." + value.replace(/\D/g, "")) >=
                    Number("0." + minTimeValue.replace(/\D/g, "")),
                );
              }
              if (maxTimeValue != null) {
                schema = schema.test(
                  "maxTime",
                  `\${label} får inte vara efter ${maxTimeValue}`,
                  (value) =>
                    // We can’t be sure if the values contain seconds, so we
                    // place the digits after the decimal point before we
                    // compare them as numbers
                    Number("0." + value.replace(/\D/g, "")) <=
                    Number("0." + maxTimeValue.replace(/\D/g, "")),
                );
              }
              break;
            // case "text":
            // case "month":
            // case "week":
            case "email":
              schema = Yup.string()
                .label(label)
                .email("${label} är inte en giltig e-postadress");
              break;
            // case "tel":
            // case "color":
            // case "search":
            case "url":
              schema = Yup.string()
                .label(label)
                .url("${label} är inte en giltig webbadress");
              break;
            default:
              schema = Yup.string().label(label);
          }
          schemaShape[kebabCase(label)] = setRequiredOrNullable(
            schema.label(label),
            required,
          );
        }
        break;
      case "checkbox":
        {
          let { label, required, values } = field;
          schemaShape[kebabCase(label)] = setRequiredOrNullable(
            Yup.array()
              .label(label)
              .of(Yup.string())
              .min(
                required ? 1 : 0,
                values.length > 1
                  ? "Välj minst ett alternativ för ${label}"
                  : 'Du måste kryssa i rutan "${label}"',
              ),
            required,
          );
        }
        break;

      case "message":
      case "radio":
      case "select":
        {
          let { label, required } = field;
          schemaShape[kebabCase(label)] = setRequiredOrNullable(
            Yup.string().label(label),
            required,
          );
        }
        break;
      default: {
        // Skip
      }
    }
  });
  return Yup.object().shape(schemaShape);
}

const rotate = keyframes`
  from {
    transform: translateZ(1px) rotateZ(0);
  }
  to {
    transform: translateZ(1px) rotateZ(360deg);
  }
`;

// eslint-disable-next-line react/prop-types
function FormModuleField({ field, ...restProps }) {
  // TODO: Check the conditional logic when this is fixed:
  // https://github.com/helsingborg-stad/modularity-form-builder/issues/18

  // const { values } = useFormikContext();
  // if (field.conditionalLogic) {
  //   const otherField = JSON.parse(field.conditonalField);
  //   Object.entries(values).find(([name, value]) => )
  // }

  // eslint-disable-next-line react/prop-types
  switch (field.fieldType) {
    case "sender": {
      return <FormModuleSenderField field={field} {...restProps} />;
    }
    case "message": {
      return <FormModuleMessageField field={field} {...restProps} />;
    }
    case "input": {
      return <FormModuleInputField field={field} {...restProps} />;
    }
    case "radio": {
      return <FormModuleRadioField field={field} {...restProps} />;
    }
    case "checkbox": {
      return <FormModuleCheckboxField field={field} {...restProps} />;
    }
    case "select": {
      return <FormModuleSelectField field={field} {...restProps} />;
    }
    // case "fileUpload": {
    //   return <FormModuleFileUploadField field={field} {...restProps} />;
    // }
    case "customContent": {
      return <FormModuleCustomContentField field={field} {...restProps} />;
    }
  }
  return null;
}

export default function FormModule(props) {
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();
  props = useThemeProps({ props, name: "MunicipioFormModule" });
  let {
    onSubmit,
    fields,
    submissionNotice,
    submitButtonText,
    disclaimer,
    spacing = [5, 7.5],
    components,
    formProps,
    ...restProps
  } = props;
  components = handleComponentsProp(components, { Wrapper: MaybeFragment });
  const { Wrapper } = components;

  const validationSchema = buildSchema({
    fields,
  });

  return (
    <Wrapper {...restProps}>
      <Formik
        initialValues={buildInitialValues({
          fields,
        })}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, status: { success, failure } = {} }) => (
          <Form {...formProps}>
            <Stack
              spacing={spacing}
              // css={css`
              //   margin-block-start: ${theme.getLength(spacing)};
              // `}
            >
              {fields.map((field, index) => {
                return <FormModuleField key={index} field={field} />;
              })}
              {disclaimer}
              {!success && (
                <div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="submit"
                  >
                    {isSubmitting ? (
                      <Fragment>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 14 14"
                          height="14"
                          width="14"
                          css={css`
                            font-size: 1.25rem;
                            width: 1em;
                            height: 1em;
                            animation: ${rotate} 1s linear infinite;
                            margin-left: -0.25rem;
                            margin-right: 0.25rem;
                          `}
                        >
                          <path
                            d="M11.7,11.49a6.5,6.5,0,1,1,0-9"
                            fill="none"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{"Skickar…"}</span>
                      </Fragment>
                    ) : (
                      submitButtonText || "Skicka"
                    )}
                  </Button>
                </div>
              )}
              {success ? (
                <Alert
                  role="alert"
                  aria-live="polite"
                  severity="success"
                  css={css`
                    margin-top: 1rem;
                  `}
                >
                  {submissionNotice}
                </Alert>
              ) : failure && !isSubmitting ? (
                <Alert
                  role="alert"
                  aria-live="polite"
                  severity="warning"
                  css={css`
                    margin-top: 1rem;
                  `}
                >
                  <p>
                    Ett fel inträffade när formuläret skulle skickas. Prova igen
                    senare.
                  </p>
                </Alert>
              ) : null}
            </Stack>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
}
